import productsData from '@/data/products.json';
import da from '@/i18n/da.json';
import type { Translations } from '@/i18n';
import type { Currency } from '@/i18n';

export interface ProductVariant {
  size: string;
  price: number;
  priceEur: number;
  stripePriceId: string;
  stripePriceIdEur: string;
  stripeRecurringPriceId: string;
  stripeRecurringPriceIdEur: string;
}

export interface ProductGuide {
  intro: string;
  bestFor: string;
  bestForWhy: string;
  notIdealFor: string;
  whoIsItFor: string;
  tip: string;
}

export interface ProductMedia {
  type: 'video' | 'image';
  src: string;
  poster?: string;
  alt?: string;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  cardLabel?: string;
  origin: string;
  roast: string;
  flavor: string;
  variety: string;
  altitude: string;
  process: string;
  description: string;
  image: string;
  cardImage?: string;
  media?: ProductMedia[];
  brewRecipe?: BrewRecipe;
  guide: ProductGuide;
  guideRichText?: string;
  variants: ProductVariant[];
  visible?: boolean;
  visibleInSubscription?: boolean;
}

export interface BrewRecipeStep {
  time: string;
  instruction: string;
}

export interface BrewRecipe {
  ratio: string;
  grind: string;
  totalTime: string;
  steps: BrewRecipeStep[];
}

type TranslatedProduct = (typeof da.products)[keyof typeof da.products];

export function applyTranslations(product: Product, translations: Translations): Product {
  const tp = translations.products[product.slug as keyof typeof translations.products] as TranslatedProduct | undefined;
  if (!tp) return product;
  const tpExtended = tp as TranslatedProduct & { altitude?: string; cardLabel?: string };
  const baseCardLabel = typeof product.cardLabel === 'string' ? product.cardLabel : '';
  const translatedCardLabel = typeof tpExtended.cardLabel === 'string' ? tpExtended.cardLabel : '';
  const cardLabel = translations._meta.locale === 'da'
    ? (baseCardLabel || translatedCardLabel)
    : translatedCardLabel;

  const translated = {
    ...product,
    name: tp.name,
    shortName: tp.shortName,
    cardLabel,
    origin: tp.origin,
    roast: tp.roast,
    flavor: tp.flavor,
    variety: tp.variety,
    altitude: tpExtended.altitude ?? product.altitude,
    process: tp.process,
    description: tp.description,
    guide: { ...product.guide, ...tp.guide },
  };

  if (tp.brewRecipe) {
    translated.brewRecipe = {
      ...product.brewRecipe,
      ratio: tp.brewRecipe.ratio,
      grind: tp.brewRecipe.grind,
      totalTime: tp.brewRecipe.totalTime,
      steps: tp.brewRecipe.steps,
    };
  }

  if ('media' in tp && product.media) {
    const mediaTp = (tp as { media?: { videoAlt?: string; imageAlt?: string } }).media;
    if (mediaTp) {
      translated.media = product.media.map((m) => ({
        ...m,
        alt: m.type === 'video' ? (mediaTp.videoAlt ?? m.alt) : (mediaTp.imageAlt ?? m.alt),
      }));
    }
  }

  const richText = (tp as { guideRichText?: string }).guideRichText;
  // Vola is generated text and may mutate HTML tags; keep the base rich text for that locale.
  if (translations._meta.locale !== 'vola' && typeof richText === 'string') {
    translated.guideRichText = richText;
  }

  return translated;
}

export function getVariantPrice(variant: ProductVariant, currency: Currency): number {
  return currency === 'eur' ? variant.priceEur : variant.price;
}

export function getVariantStripePriceId(variant: ProductVariant, currency: Currency): string {
  return currency === 'eur' ? variant.stripePriceIdEur : variant.stripePriceId;
}

export function getVariantRecurringPriceId(variant: ProductVariant, currency: Currency): string {
  return currency === 'eur' ? variant.stripeRecurringPriceIdEur : variant.stripeRecurringPriceId;
}

export function getAllProducts(translations?: Translations, includeHidden = false): Product[] {
  const t = translations ?? da;
  const all = (productsData as Product[]).map((p) => applyTranslations(p, t));
  if (includeHidden) return all;
  return all.filter((p) => p.visible !== false);
}

export function getProductBySlug(slug: string, translations?: Translations, includeHidden = false): Product | undefined {
  const t = translations ?? da;
  const product = (productsData as Product[]).find((p) => p.slug === slug);
  if (!product) return undefined;
  if (!includeHidden && product.visible === false) return undefined;
  return applyTranslations(product, t);
}

export function getPriceByStripePriceId(stripePriceId: string): number | undefined {
  for (const product of productsData as Product[]) {
    for (const variant of product.variants) {
      if (variant.stripePriceId === stripePriceId || variant.stripePriceIdEur === stripePriceId) {
        return variant.stripePriceId === stripePriceId ? variant.price : variant.priceEur;
      }
    }
  }
  return undefined;
}

export function getVariantByRecurringPriceId(stripePriceId: string): { product: Product; variant: ProductVariant } | undefined {
  for (const product of productsData as Product[]) {
    for (const variant of product.variants) {
      if (
        variant.stripeRecurringPriceId === stripePriceId
        || variant.stripeRecurringPriceIdEur === stripePriceId
      ) {
        return { product, variant };
      }
    }
  }
  return undefined;
}

export function formatPrice(priceInMinorUnits: number, translations?: Translations): string {
  const t = translations ?? da;
  const amount = priceInMinorUnits / 100;
  const isEur = t.common.priceFormat.includes('€');
  const formatted = isEur ? amount.toFixed(2) : amount.toFixed(0);
  return t.common.priceFormat.replace('{amount}', formatted);
}
